import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Lock, Mail, ArrowRight, Building, Shield, AlertCircle, CheckCircle, Activity, Monitor, Users, BarChart3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import '../Styles/login.css';
import ApiService from '../services/api';

const Login = () => {
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        rememberMe: false
    });
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [isVisible, setIsVisible] = useState(false);
    const [apiError, setApiError] = useState('');
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        setIsVisible(true);
        checkExistingAuth();
    }, [navigate]);

    // Au début du useEffect dans Login.jsx
    useEffect(() => {
        setIsVisible(true);
        
        // Solution simple : vider le localStorage à chaque fois
        localStorage.removeItem('unitech_user');
        localStorage.removeItem('unitech_token');
        
        // Réinitialiser l'état de l'ApiService
        ApiService.user = null;
        ApiService.token = null;
        
        setIsCheckingAuth(false);
    }, [navigate]);

    const checkExistingAuth = async () => {
        try {
            const syncUser = ApiService.getUserSync();
            if (syncUser) {
                console.log('User already authenticated, redirecting...');
                navigate('/dashboard');
                return;
            }

            const timeout = new Promise((_, reject) => 
                setTimeout(() => reject(new Error('timeout')), 2000)
            );
            
            const authCheck = ApiService.getCurrentUser();
            const response = await Promise.race([authCheck, timeout]);
            
            if (response.success && response.user) {
                console.log('Valid session found, redirecting...');
                navigate('/dashboard');
            }
        } catch (error) {
            console.log('No valid session, showing login form');
        } finally {
            setIsCheckingAuth(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setErrors({});
        setApiError('');

        const newErrors = {};
        if (!formData.email) {
            newErrors.email = 'Email address is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters long';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            setIsLoading(false);
            return;
        }

        try {
            const response = await ApiService.login(formData.email, formData.password);
            
            if (response.success) {
                console.log('Login successful:', response.user);

                if (formData.rememberMe) {
                    localStorage.setItem('userEmail', formData.email);
                } else {
                    localStorage.removeItem('userEmail');
                }

                navigate('/dashboard');
            } else {
                setApiError(response.message || 'Login error occurred');
            }
        } catch (error) {
            console.error('Login error:', error);
            setApiError(error.message || 'Server error during login. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
        if (apiError) {
            setApiError('');
        }
    };

    useEffect(() => {
        const savedEmail = localStorage.getItem('userEmail');
        if (savedEmail) {
            setFormData(prev => ({
                ...prev,
                email: savedEmail,
                rememberMe: true
            }));
        }
    }, []);

    if (isCheckingAuth) {
        return (
            <div className="modern-login-container">
                <div className="loading-screen">
                    <div className="loading-content">
                        <div className="loading-spinner-large"></div>
                        <p className="loading-text">Checking authentication...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="modern-login-container">
            {/* Left Side - Branding & Info */}
            <div className="login-left-panel">
                <div className="brand-section">
                    <div className="brand-logo">
                        <Building className="brand-icon" />
                    </div>
                    <h1 className="brand-title">UNITECH IKK</h1>
                    <p className="brand-subtitle">Building & Construction Materials</p>
                    <div className="brand-tagline">
                        Excellence in every project, Innovation in every solution
                    </div>
                </div>

                <div className="features-section">
                    <div className="feature-item">
                        <div className="feature-icon">
                            <Monitor />
                        </div>
                        <div className="feature-content">
                            <h3>Advanced Analytics</h3>
                            <p>Real-time insights and comprehensive reporting for better decision making</p>
                        </div>
                    </div>
                    <div className="feature-item">
                        <div className="feature-icon">
                            <Users />
                        </div>
                        <div className="feature-content">
                            <h3>Team Collaboration</h3>
                            <p>Seamless workflow management and team coordination tools</p>
                        </div>
                    </div>
                    <div className="feature-item">
                        <div className="feature-icon">
                            <BarChart3 />
                        </div>
                        <div className="feature-content">
                            <h3>Performance Tracking</h3>
                            <p>Monitor project progress and optimize operational efficiency</p>
                        </div>
                    </div>
                </div>

                <div className="decorative-illustration">
                    <div className="illustration-circle circle-1"></div>
                    <div className="illustration-circle circle-2"></div>
                    <div className="illustration-circle circle-3"></div>
                    <div className="laptop-mockup">
                        <div className="laptop-screen">
                            <div className="screen-content">
                                <div className="screen-header"></div>
                                <div className="screen-body">
                                    <div className="chart-bar bar-1"></div>
                                    <div className="chart-bar bar-2"></div>
                                    <div className="chart-bar bar-3"></div>
                                    <div className="chart-bar bar-4"></div>
                                </div>
                            </div>
                        </div>
                        <div className="laptop-base"></div>
                    </div>
                </div>
            </div>

            {/* Right Side - Login Form */}
            <div className="login-right-panel">
                <div className="login-form-container">
                    <div className="form-header">
                        <h2 className="form-title">Welcome Back</h2>
                        <p className="form-subtitle">Sign in to your enterprise dashboard</p>
                    </div>

                    <form className="professional-form" onSubmit={handleSubmit}>
                        {apiError && (
                            <div className="error-alert">
                                <AlertCircle className="error-icon" />
                                <span>{apiError}</span>
                            </div>
                        )}

                        <div className="input-group">
                            <label className="input-label" htmlFor="email">
                                Work Email
                            </label>
                            <div className="input-wrapper">
                                <Mail className="input-icon" />
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className={`professional-input ${errors.email ? 'input-error' : ''}`}
                                    placeholder="Enter your work email"
                                    autoComplete="email"
                                />
                            </div>
                            {errors.email && (
                                <div className="field-error">
                                    <AlertCircle className="field-error-icon" />
                                    <span>{errors.email}</span>
                                </div>
                            )}
                        </div>

                        <div className="input-group">
                            <label className="input-label" htmlFor="password">
                                Password
                            </label>
                            <div className="input-wrapper">
                                <Lock className="input-icon" />
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    id="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    className={`professional-input ${errors.password ? 'input-error' : ''}`}
                                    placeholder="Enter your password"
                                    autoComplete="current-password"
                                />
                                <button
                                    type="button"
                                    className="password-toggle-btn"
                                    onClick={() => setShowPassword(!showPassword)}
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                >
                                    {showPassword ? <EyeOff /> : <Eye />}
                                </button>
                            </div>
                            {errors.password && (
                                <div className="field-error">
                                    <AlertCircle className="field-error-icon" />
                                    <span>{errors.password}</span>
                                </div>
                            )}
                        </div>

                        <div className="form-options">
                            <label className="checkbox-container">
                                <input
                                    type="checkbox"
                                    name="rememberMe"
                                    checked={formData.rememberMe}
                                    onChange={handleChange}
                                    className="checkbox-input"
                                />
                                <span className="checkbox-custom">
                                    <CheckCircle className="checkbox-icon" />
                                </span>
                                <span className="checkbox-text">Remember me for 30 days</span>
                            </label>
                            <a href="#" className="forgot-link">
                                Forgot password?
                            </a>
                        </div>

                        <button
                            type="submit"
                            className="signin-button"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <div className="button-spinner"></div>
                                    <span>Signing in...</span>
                                </>
                            ) : (
                                <>
                                    <span>Sign in to Dashboard</span>
                                    <ArrowRight className="button-icon" />
                                </>
                            )}
                        </button>
                    </form>

                    <div className="form-footer">
                        <div className="security-badge">
                            <Shield className="security-icon" />
                            <div className="security-text">
                                <span className="security-title">Enterprise Security</span>
                                <span className="security-desc">256-bit SSL encryption</span>
                            </div>
                        </div>

                        <div className="status-badge">
                            <div className="status-indicator active"></div>
                            <Activity className="status-icon" />
                            <span>All Systems Operational</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;